<script setup lang="ts">
const { t } = useI18n();
const { getCraftableItems } = useCraftingController();
const { trackEvent } = useGAnalytics();
const route = useRoute();
const GFALExStore = useGFALExStore();
const appStateStore = useAppStateStore();

const scrollToCategory = (category: string) => {
  const element = document.getElementById(category);
  if (element) {
    const offset = 120;
    const elementPosition =
      element.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }
};

const { data: storeCraftables, status } = await useAsyncData(
  "getCraftableItems",
  async () => {
    if (GFALExStore.GFALExRate === 0) {
      throw new Error("Server down");
    }
    const response = await getCraftableItems(appStateStore.currentMerchant);
    const categories = new Set(
      response.records.map((item: CraftItem) => item.categoryId)
    );
    return {
      records: response.records,
      total: response.total,
      categories,
    };
  },
  {
    default: () => ({
      records: [],
      total: 0,
      categories: new Set<string>(),
    }),
    server: false,
    lazy: true,
  }
);
</script>

<template>
  <LoaderCardGridSkeleton
    v-if="
      status == 'pending' || status == 'idle' || !appStateStore.areTidsLoaded
    " />
  <template v-else>
    <template v-if="status === 'success' && false">
      <div
        class="mt-6 flex justify-center gap-6 text-neutral-300 font-bold flex-nowrap">
        <template
          v-if="storeCraftables.categories.size > 1"
          v-for="category in storeCraftables.categories">
          <p
            ref="allItems"
            @click="scrollToCategory(`CRAFT-${category}`)"
            class="w-fit text-center px-2 py-1.5 cursor-pointer whitespace-nowrap z-10 transition-all ease-in-out duration-500 hover:text-primary-500">
            {{
              $t(
                `${appStateStore.currentMerchant}.property.categoryId.values.CRAFT-${category}`,
                0
              )
            }}
          </p>
        </template>
      </div>
    </template>
    <template
      v-if="status === 'success'"
      v-for="category in storeCraftables.categories">
      <div
        class="category-gradient h-14 flex items-center justify-center mt-6 mb-2 rounded-sm">
        <h2 :id="`CRAFT-${category}`" class="text-2.5xl font-bold text-neutral-200">
          {{
            $t(
              `${appStateStore.currentMerchant}.property.categoryId.values.CRAFT-${category}`,
              0
            )
          }}
        </h2>
      </div>
      <CommonBanner :category-id="category" />
      <div
        class="grid auto-rows-fr place-content-center grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 1_5xl:grid-cols-4 gap-6 my-6">
        <template
          v-for="(craftable, index) in storeCraftables.records.filter(
            (item: CraftItem) => item.categoryId === category
          )"
          :key="craftable.id">
          <NuxtLinkLocale
            v-if="index < 7"
            :to="`/craft/${$route.params.merchant}/${craftable.id}`"
            :data-test="`link-store-item-${craftable.id}`"
            @click="
              trackEvent(`click_item_${$route.name as string}`, {
                itemId: craftable.id,
              })
            ">
            <CardCraftable :craftable="craftable" />
          </NuxtLinkLocale>

          <NuxtLinkLocale
            v-else-if="
              storeCraftables.records.filter(
                (item: CraftItem) => item.categoryId === category
              ).length === 8 && index === 7
            "
            :to="`/craft/${$route.params.merchant}/${craftable.id}`"
            :data-test="`link-store-item-${craftable.id}`"
            @click="
              trackEvent(`click_item_${$route.name as string}`, {
                itemId: craftable.id,
              })
            ">
            <CardCraftable :craftable="craftable" />
          </NuxtLinkLocale>

          <CardSeeMore v-else-if="index === 8" :categoryId="category" isCraftable="true" />
        </template>
      </div>
    </template>
  </template>
</template>

<style scoped>
.category-gradient {
  background: radial-gradient(
    circle,
    rgba(47, 47, 47, 1) 0%,
    rgba(31, 31, 31, 1) 100%
  );
}
</style>
